import { useEffect, useState, PropsWithChildren } from 'react';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import { SelectChangeEvent } from '@mui/material/Select';
import { gql } from '@yi/core';
import { makeCouponQs } from '@yi/core/src/coupons';
import { i18n_buy_now } from '@yi/i18n/phrases/i18n_buy_now';
import { i18n_preview } from '@yi/i18n/phrases/i18n_preview';

import { Snackbar, useLocale } from '~/components';
import { moreInfoPopoverZIndex } from '~/styles';

import type { PriceInfo } from './PriceInfo';

import { DiscountText } from './DiscountText';
import { MenuItem, Select, Link, PreviewButton } from './elements';

interface Props extends React.ComponentProps<typeof Snackbar> {
  buttons?: gql.typesSsr.Query_Course_yi_Course_sales_page_select['buttons'];
  title: string;
  priceInfo: PriceInfo;
  hasPreview: boolean;
  showBanner: boolean;
  setToggleBanner: (val: boolean) => void;
  setVideoPlayer: () => void;
  snackbarStyles: React.CSSProperties;
}

export const CourseCTASnackbar: React.FC<PropsWithChildren<Props>> = ({
  buttons,
  showBanner,
  onClick,
  setToggleBanner,
  title,
  hasPreview,
  priceInfo: { originalPrice: price, discount },
  setVideoPlayer,
  snackbarStyles,
}) => {
  const { locale } = useLocale();

  const [value, setValue] = useState(0);
  const [listPrice, setListPrice] = useState(price || 0);

  const onChange = (event: SelectChangeEvent<unknown>) => {
    if (typeof event.target.value !== 'number') return console.warn('Unexpected value');
    const index = event.target?.value || 0;
    setValue(index);
    if (buttons?.[index]?.price) setListPrice(buttons[index]?.price || 0);
  };

  useEffect(() => {
    if (buttons?.[0]?.price) setListPrice(buttons[0].price || 0);
  }, [buttons]);

  if (!buttons || buttons.length === 0 || listPrice === 0) return null;

  const valueButton = buttons[value];

  const couponQs = discount && 'couponCode' in discount ? makeCouponQs(discount?.couponCode) : '';
  return (
    <Snackbar
      onClick={onClick}
      open={showBanner}
      setClose={() => setToggleBanner(false)}
      textAlign="center"
      headerText={title}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      style={snackbarStyles}
    >
      <Box my={0.5} textAlign="center">
        {listPrice > 0 && (
          <Box fontSize={26} fontWeight={500} color="white" my={{ xs: 2 }}>
            <DiscountText
              price={listPrice || 0}
              discount={discount}
              testId="sales-page-snack-discount"
            />
          </Box>
        )}
        <Box
          display={{ xs: 'inline', md: 'flex' }}
          justifyContent="space-evenly"
          alignItems="center"
        >
          <Box sx={buttons && buttons.length > 1 ? { display: { xs: 'none', md: 'block' } } : {}}>
            {hasPreview && (
              <PreviewButton onClick={setVideoPlayer}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mx="2px"
                  height="100%"
                >
                  <PlayArrow style={{ height: 18, width: 18, marginRight: '4px', fill: 'white' }} />
                  <Box
                    component="span"
                    fontSize={locale === 'es' ? '11px' : '14px'}
                    fontWeight="600"
                  >
                    {i18n_preview(locale)}
                  </Box>
                </Box>
              </PreviewButton>
            )}
          </Box>

          {buttons?.length > 1 && (
            <Box mb={{ xs: 3, md: 0 }}>
              <Select
                value={value}
                onChange={onChange}
                variant="filled"
                MenuProps={{
                  style: { zIndex: moreInfoPopoverZIndex },
                }}
                style={{
                  height: '40px',
                  backgroundColor: 'transparent',
                  maxWidth: '160px',
                  minWidth: '160px',
                  width: '100%',
                }}
              >
                {buttons.map((button, index) => (
                  <MenuItem value={index}>{button?.text}</MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {buttons[0] && valueButton && (
            <Link
              as="a"
              href={
                buttons?.length > 1
                  ? `${valueButton.url}${couponQs}`
                  : `${buttons?.[0].url}${couponQs}`
              }
            >
              <Box
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                fontSize="14px"
                fontWeight="600"
              >
                {i18n_buy_now(locale)}
              </Box>
            </Link>
          )}
        </Box>
      </Box>
    </Snackbar>
  );
};
